// src/components/PerpetualEpisodeBanner.js
// FINAL: This version refactors the layout to allow the mini-calendar to take the full width.

import React, { useState } from 'react';
import { Paper, Box, Typography, Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, Chip, Divider, Alert, Stack } from '@mui/material';

// Icons
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import MedicationOutlinedIcon from '@mui/icons-material/MedicationOutlined';
import SickOutlinedIcon from '@mui/icons-material/SickOutlined';
import FitnessCenterOutlinedIcon from '@mui/icons-material/FitnessCenterOutlined';
import FastfoodOutlinedIcon from '@mui/icons-material/FastfoodOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import SentimentSatisfiedOutlinedIcon from '@mui/icons-material/SentimentSatisfiedOutlined';
import NotesIcon from '@mui/icons-material/Notes';

import ClinicianMiniCalendar from './ClinicianMiniCalendar';

const TimeSeriesChart = ({ patientId, logType }) => (
    <Box sx={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Alert severity="info">Chart placeholder for '{logType}' data. A charting component (e.g., using Recharts or Chart.js) should be implemented here to visualize the historical data.</Alert>
    </Box>
);

const formatDisplayValue = (value) => {
    if (!value) return 'N/A';
    return String(value).replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
};

const checkinIcons = {
    vitals: { component: FavoriteBorderOutlinedIcon, name: 'Vitals' },
    medication_log: { component: MedicationOutlinedIcon, name: 'Medication' },
    symptoms: { component: SickOutlinedIcon, name: 'Symptoms' },
    activity: { component: FitnessCenterOutlinedIcon, name: 'Activity' },
    diet_fluid: { component: FastfoodOutlinedIcon, name: 'Diet & Fluid' },
    journal: { component: EditNoteOutlinedIcon, name: 'Journal' },
    mood: { component: SentimentSatisfiedOutlinedIcon, name: 'Mood' },
};

const PerpetualEpisodeBanner = ({ episode, patientInfo, calendarData }) => {
    const [chartModalOpen, setChartModalOpen] = useState(false);
    const [currentChart, setCurrentChart] = useState({ logType: '', title: '' });

    const handleIconClick = (logType, title) => {
        setCurrentChart({ logType, title });
        setChartModalOpen(true);
    };

    const handleCloseModal = () => setChartModalOpen(false);

    if (!episode || !patientInfo) return null;

    return (
        <>
            <Paper elevation={0} variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2.5, mb: 4, overflow: 'hidden' }}>
                <Stack spacing={2}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 600 }}>{`${patientInfo.first_name} ${patientInfo.last_name}`} Daily Check-ins</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>SaMD SN: {episode.id}</Typography>
                        </Box>
                        <Chip label={formatDisplayValue(episode.status)} size="small" color={episode.status === 'active' ? 'success' : 'warning'} />
                    </Box>
                    
                    <Divider />

                    {/* Check-in Icons Section */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                        {(episode.mandated_daily_checkins || []).map(logType => {
                            const Icon = checkinIcons[logType]?.component || NotesIcon;
                            const name = checkinIcons[logType]?.name || formatDisplayValue(logType);

                            return (
                                <Tooltip title={`View ${name} History`} key={logType}>
                                    <Box sx={{ textAlign: 'center', m: 1, minWidth: 80 }}>
                                        <IconButton onClick={() => handleIconClick(logType, name)} sx={{ width: 64, height: 64, flexDirection: 'column', color: 'primary.main', p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, '&:hover': { bgcolor: 'action.hover', transform: 'scale(1.05)' }}}>
                                            <Icon sx={{ fontSize: 32 }} />
                                        </IconButton>
                                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'medium' }}>{name}</Typography>
                                    </Box>
                                </Tooltip>
                            );
                        })}
                    </Box>

                    {/* Mini-Calendar Section - now takes full width */}
                    <Box>
                       <ClinicianMiniCalendar calendarData={calendarData} />
                    </Box>
                </Stack>
            </Paper>

            <Dialog open={chartModalOpen} onClose={handleCloseModal} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 2 }}}>
                <DialogTitle>Historical Trend: {currentChart.title}</DialogTitle>
                <DialogContent dividers>
                    <TimeSeriesChart patientId={patientInfo.id} logType={currentChart.logType} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PerpetualEpisodeBanner;